/**
 * @file use-page-content-editor.ts
 * @module app/modules/setting/hooks/use-page-content-editor
 * @description Shared state/data logic for single-content Pages sub-tabs
 * (Terms & Conditions, Privacy Policy). Extracted to remove duplication
 * between pages-terms-condition.tsx and pages-privacy-policy.tsx.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.3.3
 * @version 1.3.3
 */

import { useEffect, useState } from 'react';
import APICallService from '../../../../api/apiCallService';
import { success } from '../../../../Global/toast';
import { Add } from '../../../../utils/constants';
import Method from '../../../../utils/methods';
import { useAuth } from '../../auth';
import { useWarehouse } from '../../../context/WarehouseContext';

interface IUsePageContentEditorParams {
  permissionModule: number;
  fetchEndpoint: any;
  saveEndpoint: any;
  successToast: string;
}

const DEFAULT_LANGUAGE = { label: 'English', title: 'English', value: 'en' };

/**
 * Provides fetch/save/language-change state for a single rich-text content page.
 * @param {IUsePageContentEditorParams} params Endpoint and permission config for the page.
 * @returns {object} State and handlers consumed by PageContentEditor.
 */
const usePageContentEditor = ({
  permissionModule,
  fetchEndpoint,
  saveEndpoint,
  successToast,
}: IUsePageContentEditorParams) => {
  const { selectedWarehouse } = useWarehouse();
  const { currentUser } = useAuth();
  const [contentData, setContentData] = useState<any>('');
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState<any>(DEFAULT_LANGUAGE);

  const hasViewPermission = !Method.hasPermission(permissionModule, Add, currentUser);

  const fetchData = async () => {
    setLoading(true);
    const apiService = new APICallService(
      fetchEndpoint,
      '',
      '',
      '',
      '',
      '',
      permissionModule,
      selectedWarehouse?.value,
    );
    const response = await apiService.callAPI();
    if (response) {
      setContentData(response);
    }
    setLoading(false);
  };

  useEffect(() => {
    (async () => {
      setFetchLoading(true);
      if (!Method.hasModulePermission(permissionModule, currentUser)) {
        window.history.back();
        return;
      }
      await fetchData();
      setFetchLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDataChange = (data: any) => {
    setContentData(data);
  };

  const handleLanguageChange = async (selectedOption: any) => {
    setSelectedLanguage(selectedOption);
    setFetchLoading(true);
    const apiService = new APICallService(
      fetchEndpoint,
      null,
      null,
      null,
      selectedOption.value,
      '',
      permissionModule,
      selectedWarehouse?.value,
    );
    const response = await apiService.callAPI();
    if (response) {
      setContentData(response);
    }
    setFetchLoading(false);
  };

  const handleSave = async (data: any) => {
    setLoading(true);
    const apiService = new APICallService(
      saveEndpoint,
      { content: data },
      null,
      null,
      selectedLanguage.value,
      '',
      permissionModule,
      selectedWarehouse?.value,
    );
    const response = await apiService.callAPI();
    if (response) {
      success(successToast);
    }
    setLoading(false);
  };

  return {
    contentData,
    loading,
    fetchLoading,
    selectedLanguage,
    hasViewPermission,
    handleDataChange,
    handleLanguageChange,
    handleSave,
  };
};

export default usePageContentEditor;
